/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import LiveLoading from 'live/components/ui/Loading'
import { StaticDataType, STATIC_DATA_STATES, GlobalNotification } from '../charts/chart-components'
import useHandleClickOutside from '../hooks/useHandleClickOutside'
import { RawInterventionEvent, StaticDataSeries, TimeSpan, WidgetData, xRangeEvent } from '../utils/type'
import { getXRangeSeries, sortFetchedStaticData, STATIC_DATA_ENTITIES } from './utils'
import { getInterventionSeries } from '../charts/intervention/InterventionChart'
import { fetchAllEntities, fetchRigs, queryEntitiesByType, RIG_API } from '../api/api'
import { DashboardContext } from 'live/context/dashboard'
import { validatePipeExpression } from 'live/components/time/span-utils'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { useThemeContext } from '@intelie/ui'
import { getTubularSeries } from '../charts/activity/ActivityChart'
import styled from 'styled-components'
import _ from 'lodash'

interface State {
    data: Array<unknown> | object
    span: Array<TimeSpan> | object
    starting: boolean
}
interface StaticDataProviderProps {
    children?: ReactNode
    state: State
    widget: any
    props: any
}

interface StaticDataContextProps {
    hasAnyButtonOpenned: boolean
    setHasAnyButtonOpenned: (x: boolean) => void
    setAddEventButtonVisible: (x: any, y: string, leftPos?: number, topPos?: number) => void
    buttonsRef: React.MutableRefObject<any>
    currentChart: any
    setButtonController: (x: any) => void
    buttonController: boolean | STATIC_DATA_STATES
    chartType: string
    tooltipPosition: { left?: number; top?: number }
    setTooltiPosition: (x: { left?: number; top?: number }) => void
    historicalMode: boolean
    setHistoricalMode: (newState: boolean | ((prevState: boolean) => boolean)) => void
    checkBlockedTrackBeforeAction: (event: any, onNotBlocked: () => void) => void
    widgetData: WidgetData
    widgetTimeSpan: TimeSpan
    updateTimeSpan: (newTimeSpan: TimeSpan) => void
    updateWidgetData: (property: string, value: StaticDataSeries) => void
    refresh: boolean
    rigId: number
    tryOpenModal: (event: xRangeEvent, entity: STATIC_DATA_ENTITIES) => { canOpen: boolean; parentId: number }
    reloadData: () => void
    liveTheme: string
    refreshWidget: () => void
}

export const StaticDataContext = createContext<StaticDataContextProps>({} as StaticDataContextProps)

export const StaticDataProvider = ({ children, widget, state, ...props }: StaticDataProviderProps): ReactElement => {
    const context = useContext(DashboardContext)
    const [hasAnyButtonOpenned, setHasAnyButtonOpenned] = useState(false)
    const [refresh, setRefresh] = useState<boolean>(false)
    const refreshWidget = (): void => setRefresh(!refresh)
    const [rigId, setRigId] = useState<number>(0)
    const [noRigSelected, setNoRigSelected] = useState(true)

    const [widgetTimeSpan, setWidgetTimeSpan] = useState<TimeSpan>({})

    const hasPermissionToOpenModal =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    const liveTheme = useThemeContext()?.theme?.themeName

    useEffect(() => {
        // fetch rigs
        if (context.state?.currentFilterValues?.asset_name_event_type) {
            fetchRigs().then(
                (
                    r: {
                        id: string
                        name: string
                        state: {
                            // eslint-disable-next-line camelcase
                            event_type: { data: string }
                        }
                    }[]
                ) => {
                    const selected = r.find(
                        x => x.name === context.state?.currentFilterValues?.asset_name_event_type[0]
                    )
                    if (!selected && (hasPermissionToOpenModal || authorization.Permission.OGPERFORMANCE_VIEW_KPI)) {
                        return globalNotification.error(`${i18n(`Unable to get lookup asset event type`)}`)
                    }
                    queryEntitiesByType(RIG_API, { assetId: { operator: '=', value: selected.id } }).then(data => {
                        if (!data || data.length === 0) {
                            return setNoRigSelected(true)
                        }
                        setRigId(Number(data[0].id))
                        setNoRigSelected(false)
                    })
                }
            )
        } else {
            globalNotification.warn(`${i18n(`An asset selected on filter is necessary`)}`)
            setNoRigSelected(true)
        }
    }, [context.state?.currentFilterValues?.asset_name_event_type])

    useEffect(() => {
        validatePipeExpression(context.params.span).then((response: { data: { start: number; end: number } }) => {
            const currentTimestamp = Date.now()
            if (response.data.start > currentTimestamp || response.data.end < response.data.start) {
                // eslint-disable-next-line no-throw-literal
                globalNotification.error(i18n('Invalid Time Span'))
                setWidgetTimeSpan({ begin: 0, end: 60000 })

                return
            }

            // context.actions.setTimespan(config.rawTimespan, undefined)
            setWidgetTimeSpan({ begin: response.data.start, end: response.data.end })
        })
    }, [context.params.span])
    const updateTimeSpan = (newTimeSpan: TimeSpan): void => {
        setWidgetTimeSpan(newTimeSpan)
    }
    // TODO: Reload after savings

    const [widgetData, setWidgetData] = useState<WidgetData>(undefined)
    // change and think about the logic here

    useEffect(() => refreshWidget, [widgetData, widget.width, widget.height])

    const handleHistoricalMode = (sortedInterventions: RawInterventionEvent[]) => {
        if (!sortedInterventions) return

        // if the first open intervention is not the last one, it means it is in history mode
        const firstOpenIdx = sortedInterventions.findIndex(i => !i.end)
        const isHistoricalMode = firstOpenIdx !== -1 && firstOpenIdx !== sortedInterventions.length - 1

        setHistoricalMode(prev => {
            if (prev !== null && !prev && isHistoricalMode) {
                globalNotification.success(
                    <GlobalNotification
                        title={i18n(`Historical event was started successfully`)}
                        description={i18n(
                            `While in Historical Data Input Mode, you will be prevented from entering data elsewhere. You must end all events in History Mode.`
                        )}
                        type="success"
                    />
                )
            }
            return isHistoricalMode
        })
    }

    const getData = useCallback((rigId: number, widgetTimeSpan: TimeSpan) => {
        fetchAllEntities(rigId, widgetTimeSpan.begin, widgetTimeSpan.end).then((data: any) => {
            data = sortFetchedStaticData(data)
            handleHistoricalMode(data?.interventions)
            const interventionData = getInterventionSeries(data?.interventions, widgetTimeSpan, rigId)
            const sectionData = getXRangeSeries(data?.sections, widgetTimeSpan, interventionData, StaticDataType.PHASE)
            const runData = getXRangeSeries(data?.runs, widgetTimeSpan, sectionData, StaticDataType.RUN)
            const activityData = getTubularSeries(data?.tubulars, widgetTimeSpan, runData, StaticDataType.ACTIVITY)
            const rigtableSeries = getXRangeSeries(
                data?.rigTables,
                widgetTimeSpan,
                interventionData,
                StaticDataType.RIG_TABLE
            )
            const wells = data?.wells
            setWidgetData({
                activitySeries: activityData,
                interventionSeries: interventionData,
                phaseSeries: sectionData,
                rigTableSeries: rigtableSeries,
                runSeries: runData,
                wells: wells
            })
        })
    }, [])
    const reloadData = useCallback(() => {
        if (!rigId || !widgetTimeSpan.begin || !widgetTimeSpan.end) return
        getData(rigId, widgetTimeSpan)
    }, [rigId, widgetTimeSpan])

    useEffect(() => {
        reloadData()
    }, [rigId, widgetTimeSpan])

    const tryOpenModal = useCallback(
        (
            event: xRangeEvent,
            entity: STATIC_DATA_ENTITIES
        ): { canOpen: boolean; parentId: number; hasPermissionToOpenModal?: number } => {
            switch (entity) {
                case STATIC_DATA_ENTITIES.INTERVENTION: {
                    return hasPermissionToOpenModal
                        ? { canOpen: true, parentId: null, hasPermissionToOpenModal: true }
                        : { canOpen: false, parentId: null, hasPermissionToOpenModal: false }
                }
                case STATIC_DATA_ENTITIES.SECTION: {
                    const intervention = widgetData.interventionSeries.data.find(
                        i =>
                            i.rawEvent.state == STATIC_DATA_STATES.STARTED_IN_EXECUTION &&
                            i.x <= event.x &&
                            event.x2 <= i.x2
                    )

                    return hasPermissionToOpenModal && intervention
                        ? {
                              canOpen: true,
                              parentId: intervention.rawEvent.id,
                              hasPermissionToOpenModal: hasPermissionToOpenModal
                          }
                        : { canOpen: false, parentId: null, hasPermissionToOpenModal: hasPermissionToOpenModal }
                }
                case STATIC_DATA_ENTITIES.RIG_TABLE: {
                    const intervention = widgetData.interventionSeries.data.find(
                        i =>
                            i.rawEvent.state == STATIC_DATA_STATES.STARTED_IN_EXECUTION &&
                            i.x <= event.x &&
                            event.x2 <= i.x2
                    )

                    return hasPermissionToOpenModal && intervention
                        ? {
                              canOpen: true,
                              parentId: intervention.rawEvent.id,
                              hasPermissionToOpenModal: hasPermissionToOpenModal
                          }
                        : { canOpen: false, parentId: null, hasPermissionToOpenModal: hasPermissionToOpenModal }
                }

                case STATIC_DATA_ENTITIES.RUN: {
                    const section = widgetData.phaseSeries.data.find(
                        p =>
                            p.rawEvent.state == STATIC_DATA_STATES.STARTED_IN_EXECUTION &&
                            p.x <= event.x &&
                            event.x2 <= p.x2
                    )

                    return hasPermissionToOpenModal && section
                        ? {
                              canOpen: true,
                              parentId: section.rawEvent.id,
                              hasPermissionToOpenModal: hasPermissionToOpenModal
                          }
                        : { canOpen: false, parentId: null, hasPermissionToOpenModal: hasPermissionToOpenModal }
                }

                case STATIC_DATA_ENTITIES.TUBULAR_TYPE: {
                    const run = widgetData.runSeries.data.find(
                        r =>
                            r.rawEvent.state == STATIC_DATA_STATES.STARTED_IN_EXECUTION &&
                            r.x <= event.x &&
                            event.x2 <= r.x2
                    )

                    return hasPermissionToOpenModal && run
                        ? {
                              canOpen: true,
                              parentId: run.rawEvent.id,
                              hasPermissionToOpenModal: hasPermissionToOpenModal
                          }
                        : { canOpen: false, parentId: null, hasPermissionToOpenModal: hasPermissionToOpenModal }
                }
                default:
                    return { canOpen: true, parentId: null, hasPermissionToOpenModal: true }
            }
        },
        [widgetData]
    )
    const updateWidgetData = (property: string, value: StaticDataSeries): void => {
        const newWidgetData = { ...widgetData }
        newWidgetData[property] = value
        setWidgetData(newWidgetData)
    }
    // Add Button States
    const [buttonController, setButtonController] = useState<boolean>(false)
    const [currentChart, setCurrentChart] = useState(null)
    const [chartType, setChartType] = useState('')
    const [tooltipPosition, setTooltiPosition] = useState(null)
    const [historicalMode, setHistoricalMode] = useState<boolean | null>(null)
    const buttonsRef = useRef(null)

    const setAddEventButtonVisible = useCallback(
        (chart, type): void => {
            const targetState = chart.rawEvent?.state
                ? chart.rawEvent?.state
                : chart.rawEvent?.end
                ? STATIC_DATA_STATES.FINISHED
                : chart.rawEvent?.start
                ? STATIC_DATA_STATES.STARTED_IN_EXECUTION
                : STATIC_DATA_STATES.EMPTY

            setChartType(type)
            setButtonController(targetState)
            setCurrentChart(chart)
        },
        [buttonController, currentChart]
    )

    const checkBlockedTrackBeforeAction = (event, onNotBlocked: () => void) => {
        if (historicalMode && event?.isRealtime) {
            globalNotification.warn(
                <GlobalNotification
                    title={i18n(`Interface blocked`)}
                    description={i18n(`You need to finish Historical Data Input Mode.`)}
                    type="warn"
                />,
                5
            )
        } else {
            onNotBlocked()
        }
    }

    useHandleClickOutside({ buttonsRef, setButtonController, currentChart })

    const value = useMemo(() => {
        return {
            hasAnyButtonOpenned,
            setHasAnyButtonOpenned,
            buttonsRef,
            setAddEventButtonVisible,
            buttonController,
            setButtonController,
            currentChart,
            chartType,
            tooltipPosition,
            setTooltiPosition,
            historicalMode,
            setHistoricalMode,
            checkBlockedTrackBeforeAction,
            widgetData,
            widgetTimeSpan,
            updateTimeSpan,
            updateWidgetData,
            refresh,
            rigId,
            tryOpenModal,
            reloadData,
            liveTheme,
            refreshWidget
        }
    }, [
        buttonsRef,
        currentChart,
        tooltipPosition,
        historicalMode,
        buttonController,
        widgetData,
        widgetTimeSpan,
        refresh,
        rigId,
        liveTheme,
        refreshWidget
    ])

    return (
        <>
            {hasPermissionToOpenModal || authorization.Permission.OGPERFORMANCE_VIEW_KPI ? (
                noRigSelected ? (
                    <NoRigSelected>{i18n('Please Select a Rig on Asset Filter')}</NoRigSelected>
                ) : widgetData ? (
                    <StaticDataContext.Provider value={value}>{children}</StaticDataContext.Provider>
                ) : (
                    <LiveLoading show={true}></LiveLoading>
                )
            ) : (
                <NoRigSelected>
                    {i18n(
                        "You don't have permission to view this dashboard. Please contact your system administrator."
                    )}
                </NoRigSelected>
            )}
        </>
    )
}

export function useStaticDataWidget(): StaticDataContextProps {
    const context = useContext(StaticDataContext)

    if (!context) {
        throw new Error(`useWidget Must be used with a ATWWidgetContext provider`)
    }

    return context
}
const NoRigSelected = styled.h1`
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    align-items: center;
`
